import React, { useEffect } from 'react';

const EFFECTIVE_DATE = '11 May 2026';
const COMPANY = 'Fly Akeed';
const APP_NAME = 'Planning Poker';
const APP_URL = 'poker.consultmobius.com';
const CONTACT_EMAIL = 'ben.c@flyakeed.com';

export default function PoliciesPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="page" style={{ maxWidth: 760 }}>

      {/* Page header */}
      <div style={{ marginBottom: 48 }}>
        <div className="flex items-center gap-3 mb-2">
          <span style={{ fontSize: '2rem' }}>📋</span>
          <h1>Legal Policies</h1>
        </div>
        <p className="text-muted">
          {APP_NAME} · {APP_URL} · Effective {EFFECTIVE_DATE}
        </p>
        <div className="toast toast-info mt-4" style={{ width: 'fit-content' }}>
          This tool is for internal use by {COMPANY} employees only.
        </div>
      </div>

      {/* Jump links */}
      <div className="card card-sm mb-8" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <a href="#privacy" style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600 }}>↓ Privacy Policy</a>
        <span style={{ color: 'var(--border)' }}>|</span>
        <a href="#terms" style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600 }}>↓ Terms of Service</a>
      </div>

      {/* ── Privacy Policy ─────────────────────────────────────── */}
      <section id="privacy" style={{ marginBottom: 64 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '2px solid var(--accent)', paddingBottom: 12, marginBottom: 32
        }}>
          <span style={{ fontSize: '1.4rem' }}>🔒</span>
          <h2>Privacy Policy</h2>
        </div>

        <PolicySection title="1. Overview">
          <p>
            {APP_NAME} ("<strong>the App</strong>") is an internal sprint estimation tool operated by {COMPANY}.
            It is accessible only to current {COMPANY} employees and is not available to the general public.
            This Privacy Policy explains what personal data the App collects, how it is used, and how it is protected.
          </p>
        </PolicySection>

        <PolicySection title="2. Who this policy applies to">
          <p>
            This policy applies exclusively to {COMPANY} employees who access the App using their
            Atlassian (Jira) account credentials. Access by any other person is not permitted.
          </p>
        </PolicySection>

        <PolicySection title="3. Data we collect">
          <p>When you sign in or use the App, we collect the following information:</p>
          <PolicyList items={[
            'Your Jira display name and email address — used to identify you as a facilitator and attribute session actions to you.',
            'Your Atlassian account ID — used internally to verify your identity.',
            'Your Jira profile avatar URL — displayed in the app interface.',
            'Jira OAuth tokens — stored temporarily in your browser session to allow the App to read and write Jira tickets on your behalf. These are never stored on our servers.',
            'Votes cast during estimation sessions — recorded against your display name for session reporting purposes.',
            'Session participation records — which sessions you joined, in what role (voter or observer), and on what date.',
          ]} />
          <p style={{ marginTop: 12 }}>
            We do not collect passwords, payment information, device identifiers, or any data
            unrelated to sprint estimation activity.
          </p>
        </PolicySection>

        <PolicySection title="4. How we use your data">
          <PolicyList items={[
            'To authenticate you as a facilitator via Atlassian OAuth 2.0.',
            'To display your name and avatar to other session participants.',
            'To attribute story point estimates and session actions to the correct team member.',
            'To generate attendance and estimation reports for team leads and facilitators.',
            'To write agreed story point values back to the corresponding Jira ticket on your behalf.',
          ]} />
        </PolicySection>

        <PolicySection title="5. Data storage">
          <p>
            Session data, votes, participants, and team records are stored in a Supabase-hosted
            PostgreSQL database. Supabase infrastructure is hosted on AWS and complies with
            standard cloud security practices. OAuth tokens are stored only in your browser's
            local storage and are never transmitted to or persisted on our database servers.
          </p>
        </PolicySection>

        <PolicySection title="6. Data sharing">
          <p>
            Your data is not sold, rented, or shared with any third party outside of {COMPANY}.
            The only external service that receives your data is Atlassian, as part of the
            standard OAuth 2.0 authentication flow you initiate when signing in.
            Supabase acts as a data processor on our behalf and does not have independent
            access to your data for any purpose.
          </p>
        </PolicySection>

        <PolicySection title="7. Data retention">
          <p>
            Session and vote data is retained for as long as the associated team exists in the App.
            When a team or session is deleted by a facilitator, all associated records are permanently removed.
            If you leave {COMPANY}, your data may be retained within historical session records
            for continuity of team reporting. You may request deletion by contacting the address below.
          </p>
        </PolicySection>

        <PolicySection title="8. Your rights">
          <p>
            As a {COMPANY} employee you have the right to request access to, correction of,
            or deletion of your personal data held by this App. To make a request, contact{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--accent)' }}>{CONTACT_EMAIL}</a>.
          </p>
        </PolicySection>

        <PolicySection title="9. Changes to this policy">
          <p>
            We may update this policy from time to time. The effective date at the top of this
            page will be updated accordingly. Continued use of the App after changes constitutes
            acceptance of the revised policy.
          </p>
        </PolicySection>

        <PolicySection title="10. Contact">
          <p>
            Questions about this Privacy Policy should be directed to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--accent)' }}>{CONTACT_EMAIL}</a>.
          </p>
        </PolicySection>
      </section>

      {/* ── Terms of Service ───────────────────────────────────── */}
      <section id="terms">
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '2px solid var(--accent)', paddingBottom: 12, marginBottom: 32
        }}>
          <span style={{ fontSize: '1.4rem' }}>📜</span>
          <h2>Terms of Service</h2>
        </div>

        <PolicySection title="1. Acceptance of terms">
          <p>
            By accessing or using {APP_NAME} ("<strong>the App</strong>") at <strong>{APP_URL}</strong>,
            you confirm that you are a current employee of {COMPANY} and that you agree to
            be bound by these Terms of Service. If you are not a {COMPANY} employee, you are
            not authorised to use this App and must exit immediately.
          </p>
        </PolicySection>

        <PolicySection title="2. Permitted use">
          <p>
            The App is provided exclusively for internal {COMPANY} business purposes, specifically
            to facilitate agile sprint planning and story point estimation. You may use the App only for
            these purposes and in accordance with {COMPANY}'s internal policies and code of conduct.
          </p>
        </PolicySection>

        <PolicySection title="3. Prohibited use">
          <p>You must not:</p>
          <PolicyList items={[
            'Share session links or access credentials with anyone outside of ' + COMPANY + '.',
            'Use the App to process any information that is confidential beyond standard internal project data.',
            'Attempt to access, modify, or interfere with data belonging to other teams or sessions.',
            'Use the App for any purpose other than sprint estimation and planning.',
            'Attempt to reverse engineer, scrape, or extract data from the App in bulk.',
          ]} />
        </PolicySection>

        <PolicySection title="4. Jira integration and data writes">
          <p>
            The App writes story point estimates directly to Jira tickets on behalf of the authorised
            facilitator. By confirming an estimate in the App, you authorise this write action.
            {COMPANY} is not responsible for incorrect estimates saved to Jira as a result of
            facilitator error. Story point values and labels (such as <code style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--bg-elevated)', padding: '1px 5px', borderRadius: 4 }}>re-estimate</code>) can
            be corrected directly in Jira at any time.
          </p>
        </PolicySection>

        <PolicySection title="5. Authentication">
          <p>
            You must authenticate using your official {COMPANY} Atlassian (Jira) account.
            You are responsible for keeping your credentials secure. Do not share your
            Atlassian credentials with anyone. If you suspect unauthorised access to your
            account, contact IT immediately.
          </p>
        </PolicySection>

        <PolicySection title="6. Availability">
          <p>
            The App is provided on a best-effort basis as an internal tool. {COMPANY} makes no
            guarantee of uptime, availability, or uninterrupted service. The App may be taken
            offline for maintenance, updates, or any other reason without notice.
          </p>
        </PolicySection>

        <PolicySection title="7. Intellectual property">
          <p>
            The App and all associated code, design, and content are the property of {COMPANY}.
            Nothing in these terms grants you any ownership rights in the App.
          </p>
        </PolicySection>

        <PolicySection title="8. Limitation of liability">
          <p>
            To the fullest extent permitted by law, {COMPANY} shall not be liable for any indirect,
            incidental, or consequential damages arising from your use of the App, including but not
            limited to incorrect story point values written to Jira. The App is an internal productivity
            tool and should not be the sole basis for project planning decisions.
          </p>
        </PolicySection>

        <PolicySection title="9. Termination">
          <p>
            {COMPANY} reserves the right to revoke access to the App at any time, for any reason,
            without notice. Access is automatically revoked upon termination of your employment
            with {COMPANY}.
          </p>
        </PolicySection>

        <PolicySection title="10. Governing law">
          <p>
            These Terms of Service are governed by the laws of the jurisdiction in which {COMPANY}
            is incorporated. Any disputes arising from use of the App shall be subject to the
            exclusive jurisdiction of the courts of that jurisdiction.
          </p>
        </PolicySection>

        <PolicySection title="11. Changes to these terms">
          <p>
            {COMPANY} reserves the right to modify these terms at any time. The effective date
            at the top of this page will reflect the most recent revision. Continued use of the
            App after changes constitutes acceptance of the revised terms.
          </p>
        </PolicySection>

        <PolicySection title="12. Contact">
          <p>
            Questions about these Terms of Service should be directed to{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--accent)' }}>{CONTACT_EMAIL}</a>.
          </p>
        </PolicySection>
      </section>

      {/* Footer */}
      <div style={{
        marginTop: 64, paddingTop: 24,
        borderTop: '1px solid var(--border)',
        textAlign: 'center'
      }}>
        <p className="text-xs text-muted">
          {APP_NAME} · {APP_URL} · Internal use only · © {new Date().getFullYear()} {COMPANY}
        </p>
      </div>

    </div>
  );
}

// ── Helper components ─────────────────────────────────────────

function PolicySection({ title, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{
        fontSize: '0.95rem',
        fontWeight: 700,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: 10
      }}>
        {title}
      </h3>
      <div style={{ color: 'var(--text-primary)', lineHeight: 1.8, fontSize: '0.95rem' }}>
        {children}
      </div>
    </div>
  );
}

function PolicyList({ items }) {
  return (
    <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
      {items.map((item, i) => (
        <li key={i} style={{
          marginBottom: 6,
          color: 'var(--text-primary)',
          lineHeight: 1.7,
          fontSize: '0.95rem'
        }}>
          {item}
        </li>
      ))}
    </ul>
  );
}
